import { Avatar, AvatarFallback, AvatarImage } from "@choose/ui/avatar";
import { Button } from "@choose/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@choose/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Skeleton } from "@choose/ui/skeleton";
import { Link } from "@tanstack/react-router";
import { EllipsisIcon, SearchSlashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  SupportRole,
  useCurrentUserQuery,
  useInternalUsersQuery,
} from "~/graphql/graphql.gen";
import type { UsersFilters } from "~/stores/users-store";
import { filterUsers, sortUsers } from "~/stores/users-store";

export function UserList({ filters }: { filters: UsersFilters }) {
  const { t } = useTranslation();

  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });

  const { data: internalUsers, isLoading } = useInternalUsersQuery(undefined, {
    select: (data) => data.internalUsers,
  });

  if (isLoading) {
    return (
      <ul>
        {Array.from({ length: 5 }).map((_, index) => (
          <li key={index} className="border-b p-4 last:border-0">
            <div className="flex items-center gap-3">
              <div className="flex flex-1 gap-3">
                <Skeleton className="size-10" />
                <div className="space-y-2">
                  <Skeleton className="h-5 min-w-20" />
                  <Skeleton className="h-4 min-w-20" />
                </div>
              </div>

              <Skeleton className="h-3 w-12" />

              <Skeleton className="h-3 w-8" />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  const viewers =
    internalUsers?.filter(
      (internalUser) =>
        internalUser.internalUser.supportRole === SupportRole.Viewer,
    ) ?? [];

  const filteredViewers = filterUsers(viewers, filters);

  const rows = filteredViewers.toSorted(sortUsers);

  const canChangeRole =
    user?.__typename === "ActorInternalUser" &&
    (user.internalUser.supportRole === SupportRole.SuperAdmin ||
      user.internalUser.supportRole === SupportRole.Admin);

  if (rows.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <SearchSlashIcon />
          </EmptyMedia>
          <EmptyTitle>{t("members.empty.title")}</EmptyTitle>
          <EmptyDescription>{t("members.empty.description")}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <ul className="rounded-xl border">
      {rows.map((internalUser) => (
        <li key={internalUser.id} className="border-b p-4 last:border-0">
          <div className="flex items-center justify-between">
            <div className="flex flex-1 items-center gap-3 truncate">
              <Avatar size="lg">
                <AvatarImage src={internalUser.internalUser.picture} />
                <AvatarFallback>
                  {internalUser.internalUser.firstName.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col truncate">
                <span className="truncate">
                  {`${internalUser.internalUser.firstName} ${internalUser.internalUser.lastName}`}
                </span>
                <span className="text-muted-foreground truncate text-sm">
                  {internalUser.internalUser.email}
                </span>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="ghost" size="sm">
                    <EllipsisIcon />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="min-w-48">
                <DropdownMenuItem
                  className="gap-2"
                  render={
                    <Link
                      to="/settings/member/$memberId"
                      params={{ memberId: internalUser.id }}
                    >
                      {t("settings.members.edit_role")}
                    </Link>
                  }
                  disabled={!canChangeRole}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </li>
      ))}
    </ul>
  );
}
